import React, { useEffect, useState } from "react";
import { Breadcrumb, Modal, Spin, Tag, Typography, notification } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { templatesApi, templatePreviewApi } from "../api";
import { ITemplate, ITemplateType } from "../types";

const { Link } = Typography;

// карточка с уменьшенным живым превью; клик — полноразмерная модалка
const SCALE = 0.24;
const FRAME_W = 1366;
const FRAME_H = 1000;

export const TemplatesPreview = () => {
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [templateTypes, setTemplateTypes] = useState<ITemplateType[]>([]);
  const [previews, setPreviews] = useState<{ [id: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [fullPreview, setFullPreview] = useState<ITemplate | null>(null);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const typesResponse = await templatesApi.loadTemplateTypes();
        setTemplateTypes(typesResponse.templateTypes);

        // собираем все страницы списка (бэкенд отдаёт по 20)
        let all: ITemplate[] = [];
        let page = 1;
        while (true) {
          const response = await templatesApi.loadTemplates(
            page,
            "all",
            "all",
            "all"
          );
          all = [...all, ...response.templates];
          if (all.length >= response.totalTemplates) {
            break;
          }
          page += 1;
        }
        setTemplates(all);
        setIsLoading(false);

        // превью грузим последовательно пачками по 3, чтобы не душить бэкенд
        const queue = [...all];
        const workers = Array.from({ length: 3 }, async () => {
          while (queue.length > 0) {
            const t = queue.shift();
            if (!t) {
              break;
            }
            try {
              const html = await templatePreviewApi.preview({
                templateId: t._id,
              });
              setPreviews((prev) => ({ ...prev, [t._id]: html }));
            } catch (e) {
              setPreviews((prev) => ({
                ...prev,
                [t._id]: "<p style='padding:20px'>Preview error</p>",
              }));
            }
          }
        });
        await Promise.all(workers);
      } catch (e) {
        notification.error({
          message: "Error",
          description: "Error loading templates",
        });
        setIsLoading(false);
      }
    };
    loadAll();
  }, []);

  const orderedTypes = [...templateTypes].sort(
    (a, b) => (a.order || 99) - (b.order || 99)
  );

  return (
    <div style={{ padding: 20, paddingTop: 10 }}>
      <Breadcrumb
        items={[
          {
            href: "/templates",
            title: (
              <>
                <ArrowLeftOutlined />
                <span>Templates</span>
              </>
            ),
          },
          { title: "Preview gallery" },
        ]}
      />
      <h2>Templates preview</h2>
      <p style={{ opacity: 0.65, marginTop: -8 }}>
        Each card is a live render with real sample-site data. Click a card to
        open it full-size.
      </p>
      {isLoading && <Spin />}
      {orderedTypes.map((tt) => {
        const ofType = templates
          .filter((t) => t.type === tt.type)
          .sort((a, b) => (a.palette || "").localeCompare(b.palette || ""));
        if (ofType.length === 0) {
          return null;
        }
        return (
          <div key={tt.type} style={{ marginBottom: 28 }}>
            <h3 style={{ marginBottom: 12 }}>
              {tt.order}. {tt.type}{" "}
              <span style={{ opacity: 0.5, fontWeight: 400 }}>
                ({ofType.length})
              </span>
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              {ofType.map((t) => (
                <div
                  key={t._id}
                  style={{
                    width: FRAME_W * SCALE + 2,
                    border: "1px solid #555",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <div
                    onClick={() => setFullPreview(t)}
                    style={{
                      width: FRAME_W * SCALE,
                      height: FRAME_H * SCALE,
                      overflow: "hidden",
                      cursor: "zoom-in",
                      background: "#fff",
                      position: "relative",
                    }}
                  >
                    {previews[t._id] ? (
                      <>
                        <iframe
                          title={t._id}
                          sandbox="allow-same-origin"
                          srcDoc={previews[t._id]}
                          scrolling="no"
                          style={{
                            width: FRAME_W,
                            height: FRAME_H,
                            border: 0,
                            transform: `scale(${SCALE})`,
                            transformOrigin: "0 0",
                            pointerEvents: "none",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                          }}
                        />
                      </>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <Spin />
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      padding: "6px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Link href={`/templates/${t._id}`}>
                      …{t._id.slice(-6)}
                    </Link>
                    {t.palette === "dark" ? (
                      <Tag color="black">dark</Tag>
                    ) : (
                      <Tag color="default">light</Tag>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Modal
        open={!!fullPreview}
        onCancel={() => setFullPreview(null)}
        footer={null}
        width="92vw"
        title={
          fullPreview
            ? `${fullPreview.type} / ${fullPreview.palette} — ${fullPreview._id}`
            : ""
        }
      >
        {fullPreview && (
          <iframe
            title="full preview"
            sandbox="allow-scripts allow-same-origin"
            srcDoc={previews[fullPreview._id] || ""}
            style={{ width: "100%", height: "80vh", border: 0 }}
          />
        )}
      </Modal>
    </div>
  );
};
